import { useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import type { Me } from '../lib/session.js';
import {
  DATE_STATUS_LABEL,
  OUTCOME_LABEL,
  REASON_LABEL,
  SEND_MODE_LABEL,
  label,
} from '../lib/labels.js';
import type { ReactionsPoll } from '../lib/labels.js';
import { date, dateTime, num, pct } from '../lib/format.js';
import { ErrorNote, Icon, Panel, Pill, Tile } from '../components/ui.js';
import type { IconName } from '../components/ui.js';
import { hrefFor } from '../lib/router.js';
import type { Screen } from '@hub/shared';

interface OverviewData {
  now: string;
  marketing: {
    contacts: number;
    reachable: number;
    withTrip: number;
    bounced: number;
    holdout: number;
    topSegments: Array<{
      code: string;
      name: string;
      member_count: number;
      push_to_provider: boolean;
      last_computed_at: string | null;
    }>;
    sent7: number;
    sent30: number;
    marketing30: number;
    service30: number;
    dryRun30: number;
    deliveredRate: number | null;
    openRate: number | null;
    clickRate: number | null;
    unsubscribeRate: number | null;
    webEvents30: number;
    webIdentifiedRate: number | null;
    webByType: Array<{ type: string; n: number }>;
    clicked30: number;
    orderedAfterClick: number;
    clickToOrderRate: number | null;
  };
  previous: {
    sent7: number;
    sent30: number;
    deliveredRate: number | null;
    openRate: number | null;
    clickRate: number | null;
    unsubscribeRate: number | null;
    suppressed30: number;
  };
  sent: { service7: number; marketing7: number };
  funnel: { sent: number; delivered: number; opened: number; clicked: number; ordered: number };
  daily: Array<{
    day: string;
    service: number;
    marketing: number;
    delivered: number;
    opened: number;
    clicked: number;
  }>;
  upcoming: {
    total: number;
    dueNow: number;
    byDay: Array<{ day: string; n: number }>;
    byCode: Array<{ code: string; name: string; n: number }>;
  };
  waiting: { held: number; exceptions: number };
  suppressed: { total30: number; topReason: { reason: string; n: number } | null };
  topMessages: Array<{
    code: string;
    name: string;
    sent: number;
    clicked: number;
    clickRate: number | null;
  }>;
  health: {
    sendMode: string;
    sendModeCeiling: string;
    simulatedProvider: boolean;
    reactionsPoll: ReactionsPoll | null;
    smartEmailing: {
      configured: boolean;
      checkedAt: string | null;
      failed: number | null;
      ok: number | null;
    };
    jobs: { failing: string[]; failed24h: number; workerAlive: boolean | null };
    templatesNotSynced: number;
    templatesMissing: number;
    lastEngineActivityAt: string | null;
    lastMessageAt: string | null;
    lastEventAt: string | null;
    inboxPending: number;
    inboxFailed: number;
  };
  tiles: {
    activeTrips: number;
    racesNext90: number;
    racesAtRisk: number;
    ticketsDeliveredByT14: number | null;
    supportPer100Trips: number | null;
    repeatPurchase: number | null;
    unsubscribeRate: number | null;
  };
  charts: {
    supportPerWeek: Array<{ week: string; per100: number; contacts: number; activeTrips: number }>;
    notSentReasons: Array<{ outcome: string; reason: string; n: number }>;
  };
  races: Array<{
    race_id: string;
    name: string;
    series: string;
    date_start: string;
    date_status: string;
    promise_by: string | null;
    race_pack_ready: boolean;
    tickets_total: number;
    tickets_delivered: number;
    active_trips: number;
    days_to_start: number;
    at_risk: boolean;
  }>;
  unsubscribes: Array<{ code: string; sent: number; unsubscribed: number; complaints: number }>;
}

type Trend = { text: string; tone: 'ok' | 'bad' | 'neu' };
const nf = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 });
const signed = (n: number): string => `${n > 0 ? '+' : n < 0 ? '−' : ''}${nf.format(Math.abs(n))}`;

/** Zmena poctu v %; `goodUp` urcuje, jestli je rust dobry. */
function countTrend(cur: number, prev: number, goodUp = true): Trend | undefined {
  if (prev <= 0) return undefined;
  const change = ((cur - prev) / prev) * 100;
  if (Math.abs(change) < 0.5) return { text: '± 0 % proti minulému období', tone: 'neu' };
  const up = change > 0;
  return {
    text: `${signed(Math.round(change))} % proti minulému období`,
    tone: up === goodUp ? 'ok' : 'bad',
  };
}

/** Zmena podilu v procentnich bodech. */
function rateTrend(cur: number | null, prev: number | null, goodUp = true): Trend | undefined {
  if (cur === null || prev === null) return undefined;
  const diff = Math.round((cur - prev) * 10) / 10;
  if (diff === 0) return { text: '± 0 p. b. proti minulému období', tone: 'neu' };
  return {
    text: `${signed(diff)} p. b. proti minulému období`,
    tone: diff > 0 === goodUp ? 'ok' : 'bad',
  };
}

const shortDay = (iso: string): string => {
  const [, m, d] = iso.split('-');
  return `${Number(d)}. ${Number(m)}.`;
};
const weekdayDay = (iso: string): string =>
  `${new Intl.DateTimeFormat('cs-CZ', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`))} ${shortDay(iso)}`;

/** Odkaz kolem dlazdice nebo bloku — cela plocha vede na prislusnou obrazovku. */
function To({ screen, param, children }: { screen: Screen; param?: string; children: ReactNode }) {
  return (
    <a className="tilelink" href={hrefFor(screen, param)}>
      {children}
    </a>
  );
}

function Section({
  icon,
  title,
  children,
  hint,
}: {
  icon: IconName;
  title: string;
  hint: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="dash-section">
      <div className="dash-section-head">
        <Icon name={icon} size={20} className="dash-section-ic" />
        <div>
          <h2>{title}</h2>
          <p>{hint}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Skeleton(): JSX.Element {
  return (
    <div aria-busy="true" aria-label="Načítám přehled">
      <div className="head page-head">
        <div>
          <h2>Přehled</h2>
          <p className="lede">Načítám přehled…</p>
        </div>
      </div>
      <div className="dash-tiles">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="skel skel-tile" />
        ))}
      </div>
      <div className="grid2" style={{ marginTop: 18 }}>
        <div className="skel skel-panel" />
        <div className="skel skel-panel" />
      </div>
    </div>
  );
}

const CHART_TIP = {
  contentStyle: {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 8,
    color: 'var(--fg)',
  },
} as const;

export function Overview({ me }: { me: Me }): JSX.Element {
  const [series, setSeries] = useState('');
  const [seller, setSeller] = useState('');
  const query = useQuery<OverviewData>({
    queryKey: ['overview', series, seller],
    queryFn: () => api.get<OverviewData>(`/api/overview${qs({ series, seller })}`),
  });

  if (query.isError) return <ErrorNote error={query.error} />;
  const data = query.data;
  if (!data) return <Skeleton />;

  const m = data.marketing;
  const prev = data.previous;
  const h = data.health;
  const tz = me.defaultTimezone;
  const nextRaces = data.races.filter((r) => r.days_to_start <= 30);
  const funnelRows = [
    { key: 'sent', name: 'Odesláno', n: data.funnel.sent, hint: 'zprávy za 30 dní' },
    { key: 'delivered', name: 'Doručeno', n: data.funnel.delivered, hint: 'dorazily do schránky' },
    { key: 'opened', name: 'Otevřeno', n: data.funnel.opened, hint: 'příjemce zprávu otevřel' },
    { key: 'clicked', name: 'Kliknuto', n: data.funnel.clicked, hint: 'klikl na odkaz ve zprávě' },
    {
      key: 'ordered',
      name: 'Objednávka',
      n: data.funnel.ordered,
      hint: 'kontakty, které do 14 dní po prokliku zaplatily',
    },
  ];
  const reasonLabel = data.suppressed.topReason
    ? label(REASON_LABEL, data.suppressed.topReason.reason)
    : null;

  return (
    <>
      <div className="head page-head">
        <div>
          <h2>Přehled</h2>
          <p className="lede">
            Jak si komunikace vede, co už odešlo, co nás čeká a jak lidé reagují.
          </p>
        </div>
        <div className="filters">
          <select value={series} onChange={(e) => setSeries(e.target.value)} aria-label="Série">
            <option value="">Všechny série</option>
            <option value="f1">F1</option>
            <option value="motogp">MotoGP</option>
          </select>
          <BrandSelect value={seller} onChange={setSeller} />
        </div>
      </div>
      {h.simulatedProvider ? (
        <div className="confirm dngc" role="alert" data-testid="simulated-provider-banner">
          <b>
            <Icon name="alert" size={16} /> Provider není napojený: zprávy se jen simulují
          </b>
          <span>
            SmartEmailing nemá zadané přístupy, takže se zprávy označují jako odeslané, ale nikomu
            nedorazí. <a href={hrefFor('settings')}>Zadejte přístupy v Nastavení → Napojení</a>.
          </span>
        </div>
      ) : null}
      {m.dryRun30 > 0 ? (
        <p className="hint">
          Z odeslaných za 30 dní je <b>{num(m.dryRun30)}</b> zpráv jen nanečisto — ven nic neodešlo.
        </p>
      ) : null}

      {/* ------------------------------------------------------ 1. KPI --- */}
      <Section
        icon="chart"
        title="Jak si vedeme"
        hint={
          <>
            Hlavní čísla za posledních 30 dní. Šipka dole ukazuje změnu proti předchozím 30 dnům —
            zelená je dobře, červená pozor. Kliknutím otevřete podrobnosti.
          </>
        }
      >
        <div className="dash-tiles">
          <To screen="communication">
            <Tile
              icon="send"
              label="Odesláno za 7 dní"
              value={num(m.sent7)}
              trend={countTrend(m.sent7, prev.sent7)}
              note={`servis ${num(data.sent.service7)} · marketing ${num(data.sent.marketing7)}`}
            />
          </To>
          <To screen="communication">
            <Tile
              icon="mail"
              label="Odesláno za 30 dní"
              value={num(m.sent30)}
              trend={countTrend(m.sent30, prev.sent30)}
              note={`servis ${num(m.service30)} · marketing ${num(m.marketing30)}`}
            />
          </To>
          <To screen="communication">
            <Tile
              icon="check"
              label="Doručeno"
              value={pct(m.deliveredRate)}
              trend={rateTrend(m.deliveredRate, prev.deliveredRate)}
              note="z odeslaných zpráv"
            />
          </To>
          <To screen="communication">
            <Tile
              icon="inbox"
              label="Otevřeno"
              value={pct(m.openRate)}
              trend={rateTrend(m.openRate, prev.openRate)}
              note="z odeslaných zpráv"
            />
          </To>
          <To screen="communication">
            <Tile
              icon="bolt"
              label="Prokliky"
              value={pct(m.clickRate)}
              trend={rateTrend(m.clickRate, prev.clickRate)}
              note="z odeslaných zpráv"
            />
          </To>
          <To screen="communication">
            <Tile
              icon="alert"
              label="Odhlášení"
              value={pct(m.unsubscribeRate)}
              trend={rateTrend(m.unsubscribeRate, prev.unsubscribeRate, false)}
              note="z marketingových zpráv"
              bad={(m.unsubscribeRate ?? 0) >= 2}
            />
          </To>
          <To screen="exceptions">
            <Tile
              icon="layers"
              label="Zadrženo / potlačeno"
              value={num(data.suppressed.total30)}
              trend={countTrend(data.suppressed.total30, prev.suppressed30, false)}
              note={
                data.suppressed.topReason
                  ? `nejčastěji: ${reasonLabel} (${num(data.suppressed.topReason.n)}×)`
                  : 'za 30 dní nic'
              }
            />
          </To>
          <To screen="customers">
            <Tile
              icon="users"
              label="Aktivní kontakty"
              value={num(m.contacts)}
              note={`${num(m.reachable)} se souhlasem · ${num(m.bounced)} s nedoručitelnou adresou`}
            />
          </To>
          <To screen="trips">
            <Tile
              icon="route"
              label="Aktivní cesty"
              value={num(data.tiles.activeTrips)}
              note={`${num(m.withTrip)} kontaktů má aspoň jednu`}
            />
          </To>
          <To screen="races">
            <Tile
              icon="flag"
              label="Závody v ohrožení"
              value={num(data.tiles.racesAtRisk)}
              note={`z ${num(data.tiles.racesNext90)} v příštích 90 dnech`}
              bad={data.tiles.racesAtRisk > 0}
            />
          </To>
        </div>
      </Section>

      {/* ----------------------------------------------- 2. Co odešlo --- */}
      <Section
        icon="send"
        title="Co odešlo"
        hint="Sloupce ukazují, kolik zpráv odešlo každý den (servisní a marketingové). Čáry ukazují, kolik z nich bylo doručeno, otevřeno a kliknuto — počítá se ke dni odeslání."
      >
        <Panel
          title="Odeslané zprávy za posledních 30 dní"
          icon="chart"
          action={
            <a className="btn" href={hrefFor('communication')}>
              Komunikace
            </a>
          }
        >
          {data.daily.every((d) => d.service + d.marketing === 0) ? (
            <p className="note">Za posledních 30 dní zatím nic neodešlo.</p>
          ) : (
            <div
              className="chart-box"
              role="img"
              aria-label={`Graf odeslaných zpráv po dnech: za 30 dní ${num(m.sent30)} zpráv, z toho ${num(m.service30)} servisních a ${num(m.marketing30)} marketingových.`}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.daily} margin={{ left: -12, right: 8, top: 8 }}>
                  <CartesianGrid stroke="var(--grid)" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={shortDay}
                    stroke="var(--muted)"
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis stroke="var(--muted)" allowDecimals={false} />
                  <Tooltip {...CHART_TIP} labelFormatter={(v: string) => weekdayDay(v)} />
                  <Legend verticalAlign="top" height={30} />
                  <Bar dataKey="service" name="Servis" stackId="s" fill="var(--c-service)" />
                  <Bar
                    dataKey="marketing"
                    name="Marketing"
                    stackId="s"
                    fill="var(--chart1)"
                    radius={[3, 3, 0, 0]}
                  />
                  <Line
                    dataKey="delivered"
                    name="Doručeno"
                    stroke="var(--c-delivered)"
                    strokeWidth={2}
                    dot={false}
                    type="monotone"
                  />
                  <Line
                    dataKey="opened"
                    name="Otevřeno"
                    stroke="var(--c-opened)"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={false}
                    type="monotone"
                  />
                  <Line
                    dataKey="clicked"
                    name="Kliknuto"
                    stroke="var(--c-clicked)"
                    strokeWidth={2}
                    strokeDasharray="2 3"
                    dot={false}
                    type="monotone"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </Section>

      {/* --------------------------------------------- 3. Co nás čeká --- */}
      <Section
        icon="calendar"
        title="Co nás čeká"
        hint="Zprávy, kterým v příštích 7 dnech nastane čas odeslání. Engine je před odesláním ještě znovu zkontroluje (souhlas, limity, sloučení), takže se konečný počet může lišit."
      >
        <div className="grid2">
          <Panel
            title="Naplánováno na 7 dní"
            icon="clock"
            subtitle={
              <>
                Celkem <b>{num(data.upcoming.total)}</b> zpráv
                {data.upcoming.dueNow > 0
                  ? `, z toho ${num(data.upcoming.dueNow)} už čeká na nejbližší běh enginu (započteno do dneška)`
                  : ''}
              </>
            }
            action={
              <a className="btn" href={hrefFor('communication')}>
                Fronta
              </a>
            }
          >
            {data.upcoming.total === 0 ? (
              <p className="note">Na příštích 7 dní nic naplánováno není.</p>
            ) : (
              <>
                <div className="chart-box small">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.upcoming.byDay} margin={{ left: -16, right: 8, top: 8 }}>
                      <CartesianGrid stroke="var(--grid)" vertical={false} />
                      <XAxis dataKey="day" tickFormatter={weekdayDay} stroke="var(--muted)" />
                      <YAxis stroke="var(--muted)" allowDecimals={false} />
                      <Tooltip
                        {...CHART_TIP}
                        labelFormatter={(v: string) => weekdayDay(v)}
                        formatter={(v: number) => [num(v), 'zpráv']}
                      />
                      <Bar dataKey="n" name="Zpráv" fill="var(--chart1)" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <ul className="codelist">
                  {data.upcoming.byCode.slice(0, 8).map((c) => (
                    <li key={c.code}>
                      <code>{c.code}</code>
                      <span>{c.name}</span>
                      <b>{num(c.n)}</b>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Panel>

          <div className="stack">
            <To screen="exceptions">
              <div
                className={`callout${data.waiting.held + data.waiting.exceptions > 0 ? ' warn' : ' ok'}`}
              >
                <Icon
                  name={data.waiting.held + data.waiting.exceptions > 0 ? 'alert' : 'check'}
                  size={22}
                />
                <div>
                  <b>Zadrženo, čeká na vyřešení</b>
                  <p>
                    {num(data.waiting.held)} zadržených zpráv · {num(data.waiting.exceptions)}{' '}
                    otevřených výjimek. Otevřít Výjimky →
                  </p>
                </div>
              </div>
            </To>
            <Panel
              title="Závody do 30 dnů"
              icon="flag"
              subtitle="Jak daleko je doručení vstupenek a jestli nehrozí problém."
              action={
                <a className="btn" href={hrefFor('races')}>
                  Všechny závody
                </a>
              }
            >
              {nextRaces.length === 0 ? (
                <p className="note">V příštích 30 dnech nezačíná žádný závod.</p>
              ) : (
                <div className="scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Závod</th>
                        <th className="n">Za</th>
                        <th className="n">Vstupenky</th>
                        <th>Stav</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nextRaces.slice(0, 6).map((r) => (
                        <tr key={r.race_id}>
                          <td>
                            <a href={hrefFor('races', r.race_id)}>{r.name}</a>
                          </td>
                          <td className="n">{num(r.days_to_start)} d</td>
                          <td className="n">
                            {r.tickets_total > 0
                              ? `${Math.round((r.tickets_delivered / r.tickets_total) * 100)} % doručeno`
                              : '—'}
                          </td>
                          <td>
                            {r.at_risk ? (
                              <Pill tone="bad">v ohrožení</Pill>
                            ) : (
                              <Pill tone="ok">v pořádku</Pill>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        </div>
      </Section>

      {/* ---------------------------------------- 4. Interakce a reakce --- */}
      <Section
        icon="bolt"
        title="Interakce a reakce"
        hint="Co se se zprávami děje po odeslání: kolik lidí je dostane, otevře, klikne a nakonec koupí. Za posledních 30 dní."
      >
        <div className="grid3">
          <Panel title="Cesta od odeslání k objednávce" icon="flow">
            <ul className="funnel">
              {funnelRows.map((f, i) => (
                <li key={f.key} title={f.hint}>
                  <div className="funnel-row">
                    <span>{f.name}</span>
                    <b>{num(f.n)}</b>
                  </div>
                  <Bar_ value={f.n} max={data.funnel.sent} />
                  <small>
                    {i === 0
                      ? f.hint
                      : `${pct(
                          funnelRows[i - 1]?.n
                            ? Math.round((f.n / (funnelRows[i - 1]?.n ?? 1)) * 1000) / 10
                            : null,
                        )} z předchozího kroku`}
                  </small>
                </li>
              ))}
            </ul>
            <p className="note">
              Poslední krok počítá kontakty, ne zprávy: kdo do 14 dní po prokliku zaplatil
              objednávku ({pct(m.clickToOrderRate)} z {num(m.clicked30)} kontaktů s prokliky).
            </p>
          </Panel>

          <Panel title="Nejlepší zprávy podle prokliků" icon="star">
            {data.topMessages.length === 0 ? (
              <p className="note">Zatím na žádnou zprávu nikdo neklikl.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Zpráva</th>
                    <th className="n">Odesláno</th>
                    <th className="n">Prokliky</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topMessages.map((t) => (
                    <tr key={t.code}>
                      <td>
                        <code>{t.code}</code> <span className="muted">{t.name}</span>
                      </td>
                      <td className="n">{num(t.sent)}</td>
                      <td className="n">{pct(t.clickRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="note">Podíl odeslaných zpráv daného kódu, na které někdo klikl.</p>
          </Panel>

          <Panel title="Aktivita na webu" icon="route">
            {m.webByType.length === 0 ? (
              <p className="note">Z webu zatím nic nechodí — napojení je vypnuté.</p>
            ) : (
              <>
                <ul className="codelist">
                  {m.webByType.map((row) => (
                    <li key={row.type}>
                      <span>{row.type}</span>
                      <b>{num(row.n)}</b>
                    </li>
                  ))}
                </ul>
                <p className="note">
                  Celkem {num(m.webEvents30)} událostí; k {pct(m.webIdentifiedRate)} z nich umíme
                  přiřadit kontakt.
                </p>
              </>
            )}
          </Panel>
        </div>
      </Section>

      {/* -------------------------------------------- 5. Zdraví systému --- */}
      <Section
        icon="plug"
        title="Zdraví systému"
        hint="Běží všechno, jak má? Tady uvidíte, jestli hub posílá ostře, jestli je napojený SmartEmailing a jestli přicházejí data z e-shopu."
      >
        <Panel>
          <div className="healthgrid">
            <To screen="settings">
              <div className="hitem">
                <small>Režim odesílání</small>
                <b>
                  <Pill
                    tone={h.sendMode === 'live' ? 'ok' : h.sendMode === 'dry_run' ? 'neu' : 'warn'}
                  >
                    {label(SEND_MODE_LABEL, h.sendMode)}
                  </Pill>
                </b>
                <em>
                  {h.sendMode === 'dry_run'
                    ? 'nic neodchází ven'
                    : h.sendMode === 'live'
                      ? 'zprávy odcházejí zákazníkům'
                      : 'jen na povolené adresy'}
                </em>
              </div>
            </To>
            <To screen="settings">
              <div className="hitem">
                <small>SmartEmailing</small>
                <b>
                  {h.simulatedProvider ? (
                    <Pill tone="bad">zprávy se jen simulují</Pill>
                  ) : !h.smartEmailing.configured ? (
                    <Pill tone="neu">nenapojeno</Pill>
                  ) : h.smartEmailing.failed ? (
                    <Pill tone="bad">{num(h.smartEmailing.failed)} chyb</Pill>
                  ) : h.smartEmailing.checkedAt ? (
                    <Pill tone="ok">v pořádku</Pill>
                  ) : (
                    <Pill tone="warn">neověřeno</Pill>
                  )}
                </b>
                <em>
                  {h.simulatedProvider
                    ? 'Provider není napojený: zprávy se jen simulují'
                    : h.smartEmailing.checkedAt
                      ? `ověřeno ${dateTime(h.smartEmailing.checkedAt, tz)}`
                      : 'kontrola schopností zatím neběžela'}
                </em>
              </div>
            </To>
            <To screen="jobs">
              <div className="hitem">
                <small>Úlohy</small>
                <b>
                  {h.jobs.workerAlive === false ? (
                    <Pill tone="bad">worker neodpovídá</Pill>
                  ) : h.jobs.failing.length > 0 || h.jobs.failed24h > 0 ? (
                    <Pill tone="bad">{num(h.jobs.failing.length || h.jobs.failed24h)} selhalo</Pill>
                  ) : (
                    <Pill tone="ok">v pořádku</Pill>
                  )}
                </b>
                <em>
                  {h.jobs.failing.length > 0
                    ? `opakovaně selhává: ${h.jobs.failing.length}`
                    : `selhání za 24 h: ${num(h.jobs.failed24h)}`}
                </em>
              </div>
            </To>
            <To screen="jobs">
              <div className="hitem" data-testid="reactions-poll">
                <small>Reakce ze SmartEmailingu</small>
                <b>
                  {!h.reactionsPoll ? (
                    <Pill tone="neu">zatím nestaženo</Pill>
                  ) : h.reactionsPoll.error ? (
                    <Pill tone="bad">chyba</Pill>
                  ) : (
                    <Pill tone="ok">staženo</Pill>
                  )}
                </b>
                <em>
                  {h.reactionsPoll
                    ? `poslední stažení ${dateTime(h.reactionsPoll.at, tz)}${
                        h.reactionsPoll.read === null
                          ? ''
                          : `, ${num(h.reactionsPoll.read)} událostí`
                      }`
                    : 'úloha běží každých 15 minut'}
                </em>
              </div>
            </To>
            <To screen="communication">
              <div className="hitem">
                <small>Šablony čekající na nahrání</small>
                <b>
                  <Pill tone={h.templatesNotSynced + h.templatesMissing > 0 ? 'warn' : 'ok'}>
                    {num(h.templatesNotSynced)}
                  </Pill>
                </b>
                <em>
                  {h.templatesMissing > 0
                    ? `marketingové, nenahrané do SmartEmailingu; ${num(h.templatesMissing)} dalších nemá šablonu`
                    : 'marketingové šablony nenahrané do SmartEmailingu'}
                </em>
              </div>
            </To>
            <To screen="data_quality">
              <div className="hitem">
                <small>Poslední aktivita enginu</small>
                <b>{h.lastEngineActivityAt ? dateTime(h.lastEngineActivityAt, tz) : '—'}</b>
                <em>
                  {h.lastMessageAt
                    ? `poslední zpráva ${dateTime(h.lastMessageAt, tz)}`
                    : 'zatím žádná zpráva'}
                </em>
              </div>
            </To>
            <To screen="data_quality">
              <div className="hitem">
                <small>Poslední událost z e-shopu</small>
                <b>{h.lastEventAt ? dateTime(h.lastEventAt, tz) : '—'}</b>
                <em>
                  {h.inboxFailed > 0 ? (
                    <Pill tone="bad">{num(h.inboxFailed)} selhalo</Pill>
                  ) : h.inboxPending > 0 ? (
                    `${num(h.inboxPending)} čeká na zpracování`
                  ) : (
                    'nic nečeká na zpracování'
                  )}
                </em>
              </div>
            </To>
          </div>
        </Panel>
      </Section>

      {/* ----------------------------------------- Provoz a podrobnosti --- */}
      <Section
        icon="flag"
        title="Provoz a podrobnosti"
        hint="Kde hrozí, že zákazník nebude vědět, co se děje: závody v ohrožení, dotazy na vstupenky a důvody, proč zpráva neodešla."
      >
        <div className="dash-tiles">
          <Tile
            icon="file"
            label="Vstupenky doručené do T−14"
            value={pct(data.tiles.ticketsDeliveredByT14)}
            note="závody, které za posledních 30 dní začaly"
          />
          <Tile
            icon="inbox"
            label="Dotazy na stav vstupenek na 100 cest"
            value={data.tiles.supportPer100Trips ?? '—'}
            note="poslední týden"
          />
          <Tile
            icon="refresh"
            label="Opakovaný nákup"
            value={pct(data.tiles.repeatPurchase)}
            note="druhý nákup do 12 měsíců"
          />
          <Tile
            icon="segment"
            label="Nákup po prokliku"
            value={pct(m.clickToOrderRate)}
            note={`${num(m.orderedAfterClick)} z ${num(m.clicked30)} do 14 dní`}
          />
        </div>

        <div className="grid2" style={{ marginTop: 18 }}>
          <Panel
            title="Dotazy na stav vstupenek na 100 cest"
            icon="inbox"
            subtitle="Po týdnech — čím méně, tím líp."
          >
            {data.charts.supportPerWeek.length === 0 ? (
              <p className="note">Zatím není z čeho počítat.</p>
            ) : (
              <div className="chart-box small">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.charts.supportPerWeek} margin={{ left: -16, right: 8 }}>
                    <CartesianGrid stroke="var(--grid)" vertical={false} />
                    <XAxis
                      dataKey="week"
                      tickFormatter={(v: string) => date(v, tz)}
                      stroke="var(--muted)"
                      minTickGap={24}
                    />
                    <YAxis stroke="var(--muted)" />
                    <Tooltip
                      {...CHART_TIP}
                      formatter={(v: number) => [String(v), 'na 100 cest']}
                      labelFormatter={(v: string) => date(v, tz)}
                    />
                    <Line
                      type="monotone"
                      dataKey="per100"
                      stroke="var(--chart1)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel
            title="Proč zpráva neodešla (30 dní)"
            icon="layers"
            subtitle="Zadržené, potlačené, sloučené a prošlé zprávy podle důvodu."
          >
            {data.charts.notSentReasons.length === 0 ? (
              <p className="note">Za posledních 30 dní nic neodpadlo.</p>
            ) : (
              <ul className="codelist">
                {data.charts.notSentReasons.slice(0, 8).map((r) => (
                  <li key={`${r.outcome}-${r.reason}`}>
                    <span>{label(REASON_LABEL, r.reason)}</span>
                    <span className="muted">{label(OUTCOME_LABEL, r.outcome)}</span>
                    <b>{num(r.n)}</b>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <Panel title="Závody v příštích 90 dnech podle rizika" icon="flag">
          {data.races.length === 0 ? (
            <p className="note">V příštích 90 dnech není žádný závod.</p>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Závod</th>
                    <th>Termín</th>
                    <th className="n">Do závodu</th>
                    <th className="n">Cest</th>
                    <th className="n">Vstupenky</th>
                    <th>Slib</th>
                    <th>Race pack</th>
                    <th>Riziko</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.races]
                    .sort(
                      (a, b) =>
                        Number(b.at_risk) - Number(a.at_risk) || a.days_to_start - b.days_to_start,
                    )
                    .map((r) => (
                      <tr key={r.race_id}>
                        <td>
                          <a href={hrefFor('races', r.race_id)}>{r.name}</a>{' '}
                          <span className="muted">{r.series}</span>
                        </td>
                        <td>
                          {date(r.date_start, tz)}{' '}
                          <span className="muted">{label(DATE_STATUS_LABEL, r.date_status)}</span>
                        </td>
                        <td className="n">{num(r.days_to_start)} d</td>
                        <td className="n">{num(r.active_trips)}</td>
                        <td className="n">
                          {r.tickets_total > 0
                            ? `${Math.round((r.tickets_delivered / r.tickets_total) * 100)} %`
                            : '—'}
                        </td>
                        <td>
                          {r.promise_by ? date(r.promise_by, tz) : <Pill tone="warn">chybí</Pill>}
                        </td>
                        <td>
                          {r.race_pack_ready ? (
                            <Pill tone="ok">připravený</Pill>
                          ) : (
                            <Pill tone="warn">chybí</Pill>
                          )}
                        </td>
                        <td>
                          {r.at_risk ? (
                            <Pill tone="bad">v ohrožení</Pill>
                          ) : (
                            <Pill tone="ok">v pořádku</Pill>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div className="grid2">
          <Panel
            title="Největší segmenty"
            icon="segment"
            action={
              <a className="btn" href={hrefFor('communication')}>
                Segmenty
              </a>
            }
          >
            {m.topSegments.length === 0 ? (
              <p className="note">
                Zatím žádné segmenty. Založíte je v <b>Komunikace → Segmenty</b>.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th className="n">Kontaktů</th>
                    <th>Do SmartEmailingu</th>
                  </tr>
                </thead>
                <tbody>
                  {m.topSegments.map((seg) => (
                    <tr key={seg.code}>
                      <td>{seg.name}</td>
                      <td className="n">{num(seg.member_count)}</td>
                      <td>
                        {seg.push_to_provider ? (
                          <Pill tone="ok">ano</Pill>
                        ) : (
                          <Pill tone="neu">ne</Pill>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel title="Odhlášení podle kódu zprávy (30 dní)" icon="alert">
            {data.unsubscribes.length === 0 ? (
              <p className="note">
                Za posledních 30 dní nebyla odeslána žádná marketingová zpráva.
              </p>
            ) : (
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Kód</th>
                      <th className="n">Odesláno</th>
                      <th className="n">Odhlášení</th>
                      <th className="n">Stížnosti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unsubscribes.map((u) => (
                      <tr key={u.code}>
                        <td>
                          <code>{u.code}</code>
                        </td>
                        <td className="n">{num(u.sent)}</td>
                        <td className="n">{num(u.unsubscribed)}</td>
                        <td className="n">{num(u.complaints)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </Section>
    </>
  );
}

/** Pruh podle podilu; `Bar` z ui.tsx ma pevny popisek, tady je potreba vizualni sirka. */
function Bar_({ value, max }: { value: number; max: number }): JSX.Element {
  const w = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <span className="fbar" aria-hidden="true">
      <i style={{ width: `${w}%` }} />
    </span>
  );
}
